<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">跨活動成效報表</h1>
      </div>
      <div class="header-actions">
        <router-link :to="{ name: 'MarketingCampaignList' }" class="btn btn-secondary">
          <i class="ri-arrow-left-line"></i>
          返回活動列表
        </router-link>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <label>開始日期</label>
        <input v-model="filters.startDate" type="date" class="form-control" />
      </div>
      <div class="filter-group">
        <label>結束日期</label>
        <input v-model="filters.endDate" type="date" class="form-control" />
      </div>
      <div class="filter-group">
        <label>狀態</label>
        <select v-model="filters.status" class="form-control">
          <option value="">全部</option>
          <option value="draft">草稿</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
          <option value="ended">已結束</option>
        </select>
      </div>
      <div class="filter-group">
        <label>來源</label>
        <select v-model="filters.creationSource" class="form-control">
          <option value="">全部</option>
          <option value="manual">手動建立</option>
          <option value="smart_marketing">智能行銷</option>
        </select>
      </div>
      <div class="search-box">
        <input
          v-model="filters.search"
          type="text"
          class="form-control"
          placeholder="搜尋活動名稱或代碼"
          @keyup.enter="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group filter-group--action">
        <button class="btn btn-primary" @click="handleSearch">查詢</button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchOverview">重試</button>
    </div>

    <template v-else>
      <div class="summary-cards">
        <div class="summary-card">
          <div class="summary-label">活動數</div>
          <div class="summary-value">{{ summary.campaignCount || 0 }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">總受眾</div>
          <div class="summary-value">{{ formatCount(summary.participants) }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">總營收</div>
          <div class="summary-value">{{ formatMoney(summary.revenue) }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">總成本</div>
          <div class="summary-value">{{ formatMoney(summary.totalCost) }}</div>
        </div>
        <div class="summary-card">
          <div class="summary-label">整體 ROI</div>
          <div class="summary-value">{{ formatRoi(summary.roi?.value) }}</div>
        </div>
      </div>

      <div class="chart-grid">
        <div class="chart-card">
          <BarChart
            title="活動營收 Top 5"
            :data="topRevenueChartData"
            x-axis-key="name"
            y-axis-key="value"
            height="320px"
            :horizontal="true"
            color="#4facfe"
          />
        </div>
        <div class="chart-card">
          <PieChart
            title="總成本結構"
            :data="costStructureChartData"
            name-key="name"
            value-key="value"
            height="320px"
            :show-label="true"
          />
        </div>
        <div class="chart-card">
          <template v-if="roiTopBottomChartData.length > 0">
            <BarChart
              title="ROI Top 5 / Bottom 5（%）"
              :data="roiTopBottomChartData"
              x-axis-key="name"
              y-axis-key="value"
              height="320px"
              :horizontal="true"
              color="#667eea"
            />
          </template>
          <div v-else class="empty-chart">
            暫無可計算 ROI 的活動資料
          </div>
        </div>
      </div>

      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>活動</th>
              <th>狀態</th>
              <th>受眾</th>
              <th>完成人數</th>
              <th>轉換訂單</th>
              <th>轉換營收</th>
              <th>總成本</th>
              <th>ROI</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="items.length === 0">
              <td colspan="9" class="empty-state">沒有符合條件的活動</td>
            </tr>
            <tr v-for="item in items" :key="item.campaignId">
              <td>
                <div class="name-cell">
                  <strong>{{ item.campaignName }}</strong>
                  <small>{{ item.campaignCode || '-' }}</small>
                </div>
              </td>
              <td>{{ statusLabel(item.status) }}</td>
              <td>{{ formatCount(item.participants) }}</td>
              <td>{{ formatCount(item.completed) }}</td>
              <td>{{ formatCount(item.conversionOrders) }}</td>
              <td>{{ formatMoney(item.conversionRevenue) }}</td>
              <td>{{ formatMoney(item.cost?.totalCost) }}</td>
              <td>{{ formatRoi(item.roi?.value) }}</td>
              <td>
                <router-link
                  :to="{ name: 'EditMarketingCampaign', params: { id: item.campaignId }, query: { tab: 'analytics' } }"
                  class="btn btn-sm btn-secondary"
                >
                  查看
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="currentPage"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="changePage"
      />
    </template>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue';
import { marketingCampaignService } from '@/api/modules/marketingCampaigns.js';
import { showError } from '@/utils/swal.js';
import BarChart from '@/components/charts/BarChart.vue';
import PieChart from '@/components/charts/PieChart.vue';

export default {
  name: 'MarketingCampaignOverview',
  components: {
    BarChart,
    PieChart
  },
  setup() {
    const loading = ref(false);
    const error = ref('');
    const items = ref([]);
    const summary = ref({});
    const currentPage = ref(1);
    const pagination = reactive({ totalPages: 1, totalItems: 0 });
    const filters = reactive({
      startDate: '',
      endDate: '',
      status: '',
      creationSource: '',
      search: ''
    });

    const buildParams = () => {
      const params = {
        page: currentPage.value,
        pageSize: 20,
        sortBy: 'createdAt',
        order: 'desc'
      };
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.status) params.status = filters.status;
      if (filters.creationSource) params.creationSource = filters.creationSource;
      if (filters.search?.trim()) params.search = filters.search.trim();
      return params;
    };

    const fetchOverview = async () => {
      loading.value = true;
      error.value = '';
      try {
        const res = await marketingCampaignService.getAnalyticsOverview(buildParams());
        const data = res.data?.data ?? res.data ?? {};
        items.value = data.items || [];
        summary.value = data.summary || {};
        Object.assign(pagination, data.pagination || { totalPages: 1, totalItems: 0 });
      } catch (e) {
        error.value = e.response?.data?.message || e.message || '載入跨活動成效失敗';
        showError(error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      fetchOverview();
    };

    const changePage = (page) => {
      currentPage.value = page;
      fetchOverview();
    };

    const formatMoney = (value) => {
      const num = Number(value || 0);
      return `$${num.toLocaleString('zh-TW', { maximumFractionDigits: 2 })}`;
    };

    const formatCount = (value) => Number(value || 0).toLocaleString('zh-TW');
    const formatRoi = (value) => {
      if (value == null) return '—';
      return `${(Number(value) * 100).toFixed(1)}%`;
    };
    const statusLabel = (status) => ({
      draft: '草稿',
      active: '啟用',
      inactive: '停用',
      ended: '已結束'
    }[status] || status || '-');

    const topRevenueChartData = computed(() => {
      return [...items.value]
        .sort((a, b) => Number(b.conversionRevenue || 0) - Number(a.conversionRevenue || 0))
        .slice(0, 5)
        .map((item) => ({
          name: item.campaignName,
          value: Number(item.conversionRevenue || 0)
        }));
    });

    const costStructureChartData = computed(() => {
      return [
        { name: '推播成本', value: Number(summary.value?.lineCost || 0) },
        { name: '點數成本', value: Number(summary.value?.pointsCost || 0) },
        { name: '券成本', value: Number(summary.value?.couponCost || 0) }
      ];
    });

    const roiTopBottomChartData = computed(() => {
      const roiItems = items.value
        .map((item) => ({
          campaignName: item.campaignName,
          roiPercent: Number(item?.roi?.value) * 100
        }))
        .filter((item) => Number.isFinite(item.roiPercent));

      if (roiItems.length === 0) return [];

      const top = [...roiItems]
        .sort((a, b) => b.roiPercent - a.roiPercent)
        .slice(0, 5)
        .map((item) => ({
          name: `TOP｜${item.campaignName}`,
          value: Number(item.roiPercent.toFixed(2))
        }));

      const topNameSet = new Set(top.map((item) => item.name.replace('TOP｜', '')));
      const bottom = [...roiItems]
        .filter((item) => !topNameSet.has(item.campaignName))
        .sort((a, b) => a.roiPercent - b.roiPercent)
        .slice(0, 5)
        .map((item) => ({
          name: `BOTTOM｜${item.campaignName}`,
          value: Number(item.roiPercent.toFixed(2))
        }));

      return [...top, ...bottom];
    });

    onMounted(fetchOverview);

    return {
      loading,
      error,
      items,
      summary,
      filters,
      currentPage,
      pagination,
      fetchOverview,
      handleSearch,
      changePage,
      formatMoney,
      formatCount,
      formatRoi,
      statusLabel,
      topRevenueChartData,
      costStructureChartData,
      roiTopBottomChartData
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.summary-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
}

.summary-label {
  color: #6c757d;
  font-size: 12px;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 20px;
  font-weight: 600;
}

.chart-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.chart-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 8px;
}

.empty-chart {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
}

.name-cell {
  display: flex;
  flex-direction: column;
}

.name-cell small {
  color: #6c757d;
}

.table-wrapper table {
  min-width: 1080px;
}
</style>
